import { ApiBaseUrl } from "@/redux/http";
import { logger } from "@/services/Logger";

/**
 * Mercure subscriber authorization (SFAPI-121).
 *
 * The hub refuses anonymous subscribers and every update is private, so each
 * EventSource needs a subscriber JWT. `GET /api/mercure/token` returns one
 * scoped to the current restaurant's topics (owners pick the restaurant with
 * the `X-Restaurant-Id` header). It is passed as the hub's `authorization`
 * query parameter, so no cross-origin cookie is involved.
 */

export const MERCURE_HUB_URL = `${ApiBaseUrl}/.well-known/mercure`;
const TOKEN_URL = `${ApiBaseUrl}/api/mercure/token`;

// Fetch a new token when the cached one has less than this left.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

type CachedToken = { token: string; expiresAt: number };

const cache = new Map<string, CachedToken>();
const inFlight = new Map<string, Promise<string | null>>();

function cacheKey(restaurantId?: string | null): string {
  return restaurantId ?? "";
}

async function fetchToken(restaurantId?: string | null): Promise<string | null> {
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (restaurantId) {
      headers["X-Restaurant-Id"] = restaurantId;
    }

    const response = await fetch(TOKEN_URL, {
      credentials: "include",
      headers,
    });
    if (!response.ok) {
      logger.error(
        "mercure",
        `Mercure token request failed with status ${response.status}`
      );
      return null;
    }

    const data = (await response.json()) as {
      token?: unknown;
      expiresAt?: unknown;
    };
    if (typeof data.token !== "string") {
      return null;
    }

    const expiresAt =
      typeof data.expiresAt === "string" ? Date.parse(data.expiresAt) : NaN;
    if (!Number.isNaN(expiresAt)) {
      cache.set(cacheKey(restaurantId), { token: data.token, expiresAt });
    }

    return data.token;
  } catch (error) {
    logger.error("mercure", "Failed to fetch the Mercure token:", error);
    return null;
  }
}

/**
 * Returns a subscriber JWT for the given restaurant, or null when none could
 * be fetched (the caller then connects without one and the hub answers 401,
 * which goes through the normal retry path).
 */
export function getMercureToken(
  restaurantId?: string | null
): Promise<string | null> {
  const key = cacheKey(restaurantId);

  const cached = cache.get(key);
  if (cached && cached.expiresAt - Date.now() > REFRESH_MARGIN_MS) {
    return Promise.resolve(cached.token);
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const request = fetchToken(restaurantId).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, request);

  return request;
}

/** Drops the cached token, e.g. after the hub refused a connection. */
export function invalidateMercureToken(restaurantId?: string | null): void {
  cache.delete(cacheKey(restaurantId));
}

export function resetMercureTokenCache(): void {
  cache.clear();
  inFlight.clear();
}

export function buildMercureUrl(topic: string, token: string | null): URL {
  const url = new URL(MERCURE_HUB_URL);
  url.searchParams.append("topic", topic);
  if (token) {
    url.searchParams.append("authorization", token);
  }

  return url;
}
